import { FILTER_URL_KEYS } from '@/src/lib/filters/filterTypes';

type ListingHrefInput = {
  aliasEncoded?: string | null;
  buildingHref?: string | null;
  hdpUrl?: string | null;
  opensBuildingPage?: boolean | null;
  uriBuilding?: string | null;
  uriMalone?: string | null;
};

type QueryInput =
  | Pick<URLSearchParams, 'get'>
  | Record<string, string | Array<string> | undefined>
  | null
  | undefined;

/**
 * Normalize a listing's `uriMalone` so the last segment is always `pad` or
 * `building` — the only second-segment values the App Router HDP routes
 * (`src/app/[locale]/(search)/[resourceOrMaloneId]/{pad,building}/page.tsx`) match.
 *
 * The HotPads API occasionally returns category-suffixed forms like
 * `/<listing-slug>/pad-for-sublet` for listings whose canonical surface
 * concatenates rental-type info onto the path. That URL doesn't match any
 * HDP route, so the App Router falls through to `[resourceOrMaloneId]/[searchSlug]`
 * which then tries to resolve the listing slug as an area and 404s with
 * "Area not found". Stripping the suffix down to `pad` keeps the listing
 * routable.
 *
 * 3-segment URIs (`/<listing-slug>/<unitNum>/pad`) already end in `pad` —
 * those are passed through unchanged and rewritten to the 2-segment form
 * by `next.config.ts`.
 *
 * Returns null for empty / undefined input so callers can fall back to an
 * alias-based URL.
 */
export function normalizeListingHref(uri: string | undefined | null): string | null {
  if (!uri) return null;
  const [path, query = ''] = uri.split('?');
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  if (segments.length === 1) {
    segments.push('pad');
  } else {
    const last = segments[segments.length - 1];
    if (last !== 'pad' && last !== 'building') {
      segments[segments.length - 1] = 'pad';
    }
  }

  const normalizedPath = `/${segments.join('/')}`;
  return query ? `${normalizedPath}?${query}` : normalizedPath;
}

function readQueryValue(query: QueryInput, key: string): string | undefined {
  if (!query) return undefined;
  if ('get' in query && typeof query.get === 'function') return query.get(key) ?? undefined;

  const raw = (query as Record<string, string | Array<string> | undefined>)[key];
  return Array.isArray(raw) ? raw[0] : raw;
}

function isBuildingHref(href: string): boolean {
  const [path] = href.split('?');
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] === 'building';
}

export function appendListingFilterParams(href: string, query: QueryInput): string {
  if (!query || !isBuildingHref(href)) return href;

  const filterParams: Record<string, string | undefined> = {};
  for (const key of FILTER_URL_KEYS) {
    const value = readQueryValue(query, key);
    if (value) filterParams[key] = value;
  }

  return appendQueryString(href, filterParams);
}

export function hasListingFilterParams(query: QueryInput): boolean {
  return FILTER_URL_KEYS.some((key) => Boolean(readQueryValue(query, key)));
}

/**
 * Append/override query params onto a path, preserving any query it already carries.
 * Array values collapse to their first entry, matching Next's searchParams shape.
 */
export function appendQueryString(path: string, query: Record<string, string | Array<string> | undefined>): string {
  const [pathname, existingQuery = ''] = path.split('?');
  const params = new URLSearchParams(existingQuery);
  for (const [key, value] of Object.entries(query)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (first !== undefined) params.set(key, first);
  }

  const queryString = params.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

export function getListingHdpHref(listing: ListingHrefInput, query?: QueryInput): string | null {
  const rawHref =
    listing.opensBuildingPage === true
      ? listing.buildingHref || listing.uriBuilding || listing.uriMalone || listing.hdpUrl
      : listing.uriMalone || listing.hdpUrl || listing.buildingHref || listing.uriBuilding;
  const href = normalizeListingHref(rawHref) || (listing.aliasEncoded ? `/listings/${listing.aliasEncoded}` : null);

  if (!href) return null;
  return appendListingFilterParams(href, query);
}
